import { useState, useCallback } from 'react';

import PropTypes from 'prop-types';

import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useError } from '#globalContexts/ErrorContext';
import { parseCSV } from '#lib/csv/csvParser';

import ColumnMappingStep from './components/ColumnMappingStep';
import FileUploadStep from './components/FileUploadStep';
import ImportConfirmStep from './components/ImportConfirmStep';
import { useCSVImport } from './hooks/useCSVImport';

const STEPS = {
	UPLOAD: 1,
	MAPPING: 2,
	CONFIRM: 3,
};

export default function CSVImportModal({ isOpen, onClose, onImportSuccess }) {
	const { showSuccess, showError } = useError();

	// Step management
	const [currentStep, setCurrentStep] = useState(STEPS.UPLOAD);

	// File data
	const [fileData, setFileData] = useState(null);
	const [allRows, setAllRows] = useState([]);

	// Column mapping
	const [columnMapping, setColumnMapping] = useState({
		companyName: null,
		websiteUrl: null,
		addressStreet: null,
		addressPostal: null,
		addressCity: null,
	});

	// Import result
	const [importResult, setImportResult] = useState(null);

	// Import mutation
	const importMutation = useCSVImport({
		onSuccess: (result) => {
			setImportResult(result);
			if (result.imported > 0) {
				const errorCount = result.errors?.length || 0;
				const suffix = errorCount > 0 ? ` (${errorCount} fehlerhaft)` : '';
				showSuccess({
					title: 'Import erfolgreich',
					message: `${result.imported} Firmen wurden importiert${suffix}`,
					autoClose: true,
					autoCloseDuration: 5000,
				});
			}
		},
		onError: (error) => {
			showError({
				title: 'Import fehlgeschlagen',
				error,
			});
		},
	});

	// Handle file loaded from FileUploadStep
	const handleFileLoaded = useCallback((data) => {
		if (data) {
			setFileData(data);

			// Parse all rows for import
			const { rows } = parseCSV(data.rawContent, 0);
			setAllRows(rows);

			// Auto-detect column mapping based on common header names
			const autoMapping = {};
			const lowerHeaders = data.headers.map((h) => h.toLowerCase());

			// Company name detection
			const nameIndex = lowerHeaders.findIndex(
				(h) => h.includes('firma') || h.includes('company') || h.includes('name') || h.includes('unternehmen'),
			);
			if (nameIndex >= 0) autoMapping.companyName = data.headers[nameIndex];

			// Website detection
			const websiteIndex = lowerHeaders.findIndex(
				(h) => h.includes('website') || h.includes('url') || h.includes('domain') || h.includes('web'),
			);
			if (websiteIndex >= 0) autoMapping.websiteUrl = data.headers[websiteIndex];

			// Street detection
			const streetIndex = lowerHeaders.findIndex(
				(h) => h.includes('straße') || h.includes('strasse') || h.includes('street') || h.includes('adresse'),
			);
			if (streetIndex >= 0) autoMapping.addressStreet = data.headers[streetIndex];

			// Postal code detection
			const postalIndex = lowerHeaders.findIndex(
				(h) => h.includes('plz') || h.includes('postal') || h.includes('zip') || h.includes('postleitzahl'),
			);
			if (postalIndex >= 0) autoMapping.addressPostal = data.headers[postalIndex];

			// City detection
			const cityIndex = lowerHeaders.findIndex(
				(h) => h.includes('stadt') || h.includes('city') || h.includes('ort'),
			);
			if (cityIndex >= 0) autoMapping.addressCity = data.headers[cityIndex];

			setColumnMapping((prev) => ({ ...prev, ...autoMapping }));
		} else {
			setFileData(null);
			setAllRows([]);
		}
	}, []);

	// Handle import
	const handleImport = useCallback(() => {
		if (!fileData || allRows.length === 0) return;

		// Filter out empty mappings
		const cleanMapping = Object.fromEntries(Object.entries(columnMapping).filter(([, v]) => v));

		importMutation.mutate({
			queryName: fileData.fileName.replace(/\.csv$/i, ''),
			columnMapping: cleanMapping,
			companies: allRows,
		});
	}, [fileData, allRows, columnMapping, importMutation]);

	// Handle close
	const handleClose = useCallback(() => {
		// Reset state
		setCurrentStep(STEPS.UPLOAD);
		setFileData(null);
		setAllRows([]);
		setColumnMapping({
			companyName: null,
			websiteUrl: null,
			addressStreet: null,
			addressPostal: null,
			addressCity: null,
		});
		setImportResult(null);

		// Call onImportSuccess if we had a successful import
		if (importResult?.success && importResult?.imported > 0) {
			onImportSuccess?.(importResult.prospectQueryId);
		}

		onClose();
	}, [onClose, importResult, onImportSuccess]);

	// Step navigation
	const goToStep = (step) => setCurrentStep(step);

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="l">
			<Panel.Header onClose={handleClose}>CSV importieren</Panel.Header>

			<div className="flex items-center px-5 py-3.5 border-b border-bgBase-400 flex-shrink-0">
				{[
					{ step: STEPS.UPLOAD, label: 'Datei' },
					{ step: STEPS.MAPPING, label: 'Zuordnung' },
					{ step: STEPS.CONFIRM, label: 'Import' },
				].map(({ step, label }, index) => (
					<div key={step} className="flex items-center">
						<div
							className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
								currentStep >= step ? 'bg-primary-500 text-white' : 'bg-bgBase-400 text-text'
							}`}
						>
							{step}
						</div>
						<span className={`ml-2 text-sm ${currentStep >= step ? 'text-text' : 'text-bgBase-800'}`}>
							{label}
						</span>
						{index < 2 && (
							<div
								className={`w-12 h-0.5 mx-3 ${currentStep > step ? 'bg-primary-500' : 'bg-bgBase-400'}`}
							/>
						)}
					</div>
				))}
			</div>

			<Panel.Body className="min-h-[400px]">
				{currentStep === STEPS.UPLOAD && (
					<FileUploadStep onFileLoaded={handleFileLoaded} onNext={() => goToStep(STEPS.MAPPING)} />
				)}

				{currentStep === STEPS.MAPPING && fileData && (
					<ColumnMappingStep
						headers={fileData.headers}
						previewRows={fileData.previewRows}
						columnMapping={columnMapping}
						onMappingChange={setColumnMapping}
						onNext={() => goToStep(STEPS.CONFIRM)}
						onBack={() => goToStep(STEPS.UPLOAD)}
					/>
				)}

				{currentStep === STEPS.CONFIRM && fileData && (
					<ImportConfirmStep
						fileName={fileData.fileName}
						totalRows={fileData.totalRows}
						columnMapping={columnMapping}
						isLoading={importMutation.isPending}
						importResult={importResult}
						onImport={handleImport}
						onBack={() => goToStep(STEPS.MAPPING)}
						onClose={handleClose}
					/>
				)}
			</Panel.Body>
		</Modal>
	);
}

CSVImportModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onImportSuccess: PropTypes.func,
};
